import { c as _c } from "react/compiler-runtime";
import React, { use, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import notFoundNewConsumerApp from 'commons/notFoundNewConsumerApp';
import PDPDesktop from './PDPDesktop';
import PDPMobile from './PDPMobile';
import PDPDesktopRevamp from './PDPDesktopRevamp';
import PDPMobileRevamp from './PDPMobileRevamp';
import useIsLayoutRevampVariant from './useIsLayoutRevampVariant';
import MobileAndTabletOnly from '../MobileAndTabletOnly';
import DesktopOnly from '../DesktopOnly';
import PDPSEOTags from './PDPSEOTags';
import PDPFooter from './PDPFooter';
import PDPContext from './state/context/PDPContext';
import PDPStickyButtonsProvider from './state/context/PDPStickyButtonsProvider';
import usePDPLuxAnalytics from './hooks/usePDPLuxAnalytics';
import usePDPCreateContextValue from './hooks/usePDPCreateContextValue';
import PDPPageAnalytics from './PDPPageAnalytics';

/**
 * Options + context creation + Provider + above/below-fold tree.
 * Resolves server options/basicDetails promises via use(); hero paint stays in
 * PDPHeroShell. Zip stock / order ETA must not suspend this region.
 */
const PDPContextRegion = t0 => {
  const $ = _c(48);
  const {
    slugOverride: t1,
    isEditOrder: t2,
    pdpIsModularBYOProduct: t3,
    productOptionsPromise,
    productBasicDetailsPromise,
    productIdentityPromise: t4,
    productHeroImageFromServer: t5,
    productCylindoHeroImage: t6
  } = t0;
  const slugOverride = t1 === undefined ? null : t1;
  const isEditOrder = t2 === undefined ? false : t2;
  const pdpIsModularBYOProduct = t3 === undefined ? false : t3;
  const productIdentityPromise = t4 === undefined ? null : t4;
  const productHeroImageFromServer = t5 === undefined ? null : t5;
  const productCylindoHeroImage = t6 === undefined ? null : t6;
  const params = useParams();
  const slug = slugOverride ?? params.slug ?? null;
  const productOptions = productOptionsPromise ? use(productOptionsPromise) : null;
  const productBasicDetails = productBasicDetailsPromise ? use(productBasicDetailsPromise) : null;
  const productIdentity = productIdentityPromise ? use(productIdentityPromise) : null;
  const t7 = Boolean(productIdentity?.basicDetails?.name);
  let t8;
  if ($[0] !== isEditOrder || $[1] !== pdpIsModularBYOProduct || $[2] !== productBasicDetails || $[3] !== productOptions || $[4] !== slug || $[5] !== t7) {
    t8 = {
      isEditOrder,
      pdpIsModularBYOProduct,
      slug,
      productOptions,
      productBasicDetails,
      isServerTitleResolved: t7
    };
    $[0] = isEditOrder;
    $[1] = pdpIsModularBYOProduct;
    $[2] = productBasicDetails;
    $[3] = productOptions;
    $[4] = slug;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const pdpContextValue = usePDPCreateContextValue(t8);
  usePDPLuxAnalytics();
  const showLayoutRevamp = useIsLayoutRevampVariant(!pdpIsModularBYOProduct && !isEditOrder);
  let t9;
  bb0: {
    if (productHeroImageFromServer?.src) {
      t9 = productHeroImageFromServer;
      break bb0;
    }
    const heroUrl = pdpContextValue.basicDetails?.data?.hero_images?.[0]?.img?.url;
    if (!heroUrl) {
      t9 = null;
      break bb0;
    }
    const t10 = pdpContextValue.basicDetails?.data?.name ? String(pdpContextValue.basicDetails.data.name) : "";
    let t11;
    if ($[7] !== heroUrl || $[8] !== t10) {
      t11 = {
        src: heroUrl,
        width: 1600,
        height: 2400,
        alt: t10
      };
      $[7] = heroUrl;
      $[8] = t10;
      $[9] = t11;
    } else {
      t11 = $[9];
    }
    t9 = t11;
  }
  const productHeroImage = t9;
  if (pdpContextValue.basicDetails.isProductNotFound) {
    let t10;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = notFoundNewConsumerApp();
      $[10] = t10;
    } else {
      t10 = $[10];
    }
    return t10;
  }
  let t10;
  if ($[11] !== pdpContextValue.stockAvailability?.data) {
    t10 = pdpContextValue.stockAvailability?.data || {};
    $[11] = pdpContextValue.stockAvailability?.data;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const {
    discontinuedCategoryMsg: t11,
    isDiscontinued: t12,
    isOutOfStock: t13,
    showNotification
  } = t10;
  const discontinuedCategoryMsg = t11 === undefined ? null : t11;
  const isDiscontinued = t12 === undefined ? false : t12;
  const isOutOfStock = t13 === undefined ? false : t13;
  let t14;
  if ($[13] !== isOutOfStock) {
    t14 = <PDPSEOTags isOutOfStock={isOutOfStock} />;
    $[13] = isOutOfStock;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] !== discontinuedCategoryMsg || $[16] !== isDiscontinued || $[17] !== isEditOrder || $[18] !== isOutOfStock || $[19] !== pdpIsModularBYOProduct || $[20] !== productCylindoHeroImage || $[21] !== productHeroImage || $[22] !== showLayoutRevamp || $[23] !== showNotification || $[24] !== slug) {
    t15 = <MobileAndTabletOnly>{showLayoutRevamp ? <PDPMobileRevamp slug={slug} isOutOfStock={isOutOfStock} showOutOfStockNotification={showNotification} showDiscontinuedNotification={isDiscontinued} discontinuedCategoryMsg={isDiscontinued ? discontinuedCategoryMsg : null} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /> : <PDPMobile slug={slug} isEditOrder={isEditOrder} isOutOfStock={isOutOfStock} showOutOfStockNotification={showNotification} pdpIsModularBYOProduct={pdpIsModularBYOProduct} showDiscontinuedNotification={isDiscontinued} discontinuedCategoryMsg={isDiscontinued ? discontinuedCategoryMsg : null} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} />}</MobileAndTabletOnly>;
    $[15] = discontinuedCategoryMsg;
    $[16] = isDiscontinued;
    $[17] = isEditOrder;
    $[18] = isOutOfStock;
    $[19] = pdpIsModularBYOProduct;
    $[20] = productCylindoHeroImage;
    $[21] = productHeroImage;
    $[22] = showLayoutRevamp;
    $[23] = showNotification;
    $[24] = slug;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== discontinuedCategoryMsg || $[27] !== isDiscontinued || $[28] !== isEditOrder || $[29] !== isOutOfStock || $[30] !== pdpIsModularBYOProduct || $[31] !== productCylindoHeroImage || $[32] !== productHeroImage || $[33] !== showLayoutRevamp || $[34] !== showNotification || $[35] !== slug) {
    t16 = <DesktopOnly>{showLayoutRevamp ? <PDPDesktopRevamp slug={slug} isOutOfStock={isOutOfStock} showOutOfStockNotification={showNotification} showDiscontinuedNotification={isDiscontinued} discontinuedCategoryMsg={isDiscontinued ? discontinuedCategoryMsg : null} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /> : <PDPDesktop slug={slug} isEditOrder={isEditOrder} isOutOfStock={isOutOfStock} showOutOfStockNotification={showNotification} pdpIsModularBYOProduct={pdpIsModularBYOProduct} showDiscontinuedNotification={isDiscontinued} discontinuedCategoryMsg={isDiscontinued ? discontinuedCategoryMsg : null} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} />}</DesktopOnly>;
    $[26] = discontinuedCategoryMsg;
    $[27] = isDiscontinued;
    $[28] = isEditOrder;
    $[29] = isOutOfStock;
    $[30] = pdpIsModularBYOProduct;
    $[31] = productCylindoHeroImage;
    $[32] = productHeroImage;
    $[33] = showLayoutRevamp;
    $[34] = showNotification;
    $[35] = slug;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <PDPPageAnalytics />;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== showLayoutRevamp) {
    t18 = !showLayoutRevamp && <PDPFooter />;
    $[38] = showLayoutRevamp;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t14 || $[41] !== t15 || $[42] !== t16 || $[43] !== t18) {
    t19 = <PDPStickyButtonsProvider>{t14}{t15}{t16}{t17}{t18}</PDPStickyButtonsProvider>;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== pdpContextValue || $[46] !== t19) {
    t20 = <PDPContext.Provider value={pdpContextValue}>{t19}</PDPContext.Provider>;
    $[45] = pdpContextValue;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  return t20;
};
PDPContextRegion.propTypes = {
  slugOverride: PropTypes.string,
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  productOptionsPromise: PropTypes.object,
  productBasicDetailsPromise: PropTypes.object,
  productIdentityPromise: PropTypes.object,
  productHeroImageFromServer: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};
export default PDPContextRegion;